import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ScheduleStatus, dayKeyInTimeZone, type OverviewResponse } from '@construction/shared';
import { api } from '@/api/client';
import { drawingsApi, scheduleApi, type Site } from '@/api/endpoints';
import { EmptyState, ListGroup, ListRow, Spinner } from '@/components/ui';
import { SiteSwitcher } from '@/components/SiteSwitcher';
import { activeOn } from '@/features/schedule/gantt-layout';
import { QueueBar } from '@/features/upload-queue';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useSiteLock } from '@/hooks/useSiteLock';
import { cn } from '@/lib/cn';
import { pickMobileTabs, visibleEntries, type VisibleEntry } from '@/lib/routes/logic';
import type { HomeCardId } from '@/lib/routes/types';
import { useAuthStore } from '@/store/auth';

/**
 * 手机端首页（CR-007 / CR-010，CR-040 合并成一个，CR-044 重排，CR-082 按示例重做）。
 *
 * 从上到下：日期 + 问候 → 当前工地 → **打卡**（整块主色，全 App 唯一的大按钮）→ 三格数字（在岗 / 待办 / 今日日报）
 * → 「这个工地」一组入口（工地概览、进度表、图纸、考勤），每行下面一句现在的情况。
 * 客户原话「为了能点击到导致页面像老年机的排版」—— 改之前是一格一格的大卡片。
 *
 * **每个入口由路由表按权限决定出不出现**（`homeCard`），每个入口自己查自己的数，
 * 不出现就不查 —— 不会调没权限的接口。看不了的数字是 null（CR-039），那一格不出现。
 *
 * CR-044 客户原话：「app中底部tabs里永久需要放置 工地概览，隐蔽工程，日报，采购，我的
 * 剩余的 代办，图纸，进度表，打卡（考勤），工地概览 都需要放在 首页」，
 * 后来又定「我需要统一首页都是/m ，app里任何人都是看见首页。/sites/:siteId 作为和图纸 进度表一样的放在首页」。
 * 顺序（CR-045「有权限就显示，无权限就不显示」）：打卡 → 待办 → 工地概览 → 图纸 → 进度表 → 考勤，由路由表 `homeCard` 的 order 决定。
 * 超管不给打卡（约束 7）。首页不是打卡页（CR-007）：打卡是一个动作，不该占掉唯一的落脚点。
 */
export function MobileHomePage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const { site, siteId, sites, isLoading, setSite, locked } = useMobileSite();

  const cards = visibleEntries(role, 'homeCard');
  const byId = (id: HomeCardId) => cards.find((c) => c.entry.card === id) ?? null;
  const punch = byId('punch');
  const review = byId('review');
  const entries = cards.filter((c) => c.entry.card !== 'punch' && c.entry.card !== 'review');
  const tabs = pickMobileTabs(role).middle;
  const logsTab = tabs.find((m) => m.id === 'logs') ?? null;
  const needsOverview = review !== null || byId('overview') !== null || byId('attendance') !== null;

  // 待办、工地概览、考勤共用一次概览请求（CR-039：字段按权限返回，看不了的是 null）
  const overview = useQuery({
    queryKey: ['overview', siteId],
    queryFn: () => api.get<OverviewResponse>(`/sites/${siteId}/overview`),
    enabled: siteId !== null && needsOverview,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }

  // 没被分到任何工地就什么也做不了 —— 服务端只返回本人参与的工地
  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintWorker')} />;
  }

  const data = overview.data ?? null;
  const today = data?.today ?? null;
  const stats: Array<{ key: string; label: string; value: ReactNode; to?: string; danger?: boolean }> = [];
  if (today && today.onSite !== null) {
    stats.push({
      key: 'onSite',
      label: t('home.statOnSite'),
      value: (
        <>
          {today.onSite}
          {today.memberCount !== null && (
            <small className="ml-0.5 text-body font-medium text-ink-3">/ {today.memberCount}</small>
          )}
        </>
      ),
      to: byId('overview')?.route.path,
    });
  }
  if (review) {
    const total = data?.pending?.total ?? null;
    stats.push({
      key: 'todo',
      label: t('home.statTodo'),
      value: total ?? '—',
      danger: (total ?? 0) > 0,
      to: review.route.path,
    });
  }
  if (today && today.logs !== null) {
    stats.push({ key: 'logs', label: t('home.statLogs'), value: today.logs, to: logsTab?.route.path });
  }

  return (
    <>
      {/* 顶上：日期一行小字、问候一行大字，下面是当前工地。工地卡片点开是系统的选择器；在班期间锁住（CR-015） */}
      <header className="mb-4">
        <p className="text-body text-ink-3">
          {new Intl.DateTimeFormat(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date())}
        </p>
        <h1 className="mt-0.5 text-heading font-bold tracking-tight text-ink">
          {greeting(t, user?.name ?? '')}
        </h1>
        <div className="mt-3.5">
          <SiteSwitcher
            id="home-site"
            size="hero"
            sites={sites}
            current={site}
            locked={locked}
            onChange={(next) => setSite(next)}
          />
        </div>
      </header>

      <div className="mb-3">
        <QueueBar />
      </div>

      {punch && <PunchHero to={punch.route.path} siteId={site.id} />}

      {stats.length > 0 && (
        <div
          className={cn(
            'mt-3 grid gap-2',
            stats.length === 3 ? 'grid-cols-3' : stats.length === 2 ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {stats.map((stat) => {
            const body = (
              <>
                <span className="block text-caption text-ink-3">{stat.label}</span>
                <span
                  className={cn(
                    'mt-0.5 block text-figure font-semibold leading-tight tracking-tight tabular-nums',
                    stat.danger ? 'text-danger' : 'text-ink',
                  )}
                >
                  {overview.isLoading ? '…' : stat.value}
                </span>
              </>
            );
            const box = 'block rounded-xl border border-rule bg-surface px-3 py-2.5';
            return stat.to ? (
              <Link key={stat.key} to={stat.to} data-entry={`home.${stat.key}`} className={cn(box, 'active:bg-surface-2')}>
                {body}
              </Link>
            ) : (
              <div key={stat.key} className={box}>
                {body}
              </div>
            );
          })}
        </div>
      )}

      {entries.length > 0 && (
        <ListGroup title={t('home.groupSite')} className="mt-5">
          {entries.map((card) => (
            <EntryRow key={card.route.id} card={card} site={site} overview={data} loading={overview.isLoading} />
          ))}
        </ListGroup>
      )}

      {/*
        一个入口都没有：功能全在底部栏的人（只写日报、只拍隐蔽工程）看到的是一句指路，
        不是「没有可用功能」—— 那句话对他是错的。真的什么都没有才说那句。
      */}
      {cards.length === 0 &&
        (tabs.length > 0 ? (
          <p className="px-1 py-6 text-body text-ink-3">{t('home.useTabs')}</p>
        ) : (
          <EmptyState title={t('home.nothingHere')} />
        ))}
    </>
  );
}

/** 「这个工地」里的一行：入口名 + 一句现在的情况 */
function EntryRow({
  card,
  site,
  overview,
  loading,
}: {
  card: VisibleEntry;
  site: Site;
  overview: OverviewResponse | null;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const id = card.entry.card as HomeCardId;
  const to = card.route.path;
  switch (id) {
    case 'overview': {
      const today = overview?.today;
      const parts = [
        today && today.onSite !== null
          ? t('home.overviewOnSite', { onSite: today.onSite, total: today.memberCount ?? '—' })
          : null,
        today && today.logs !== null ? t('home.overviewLogs', { count: today.logs }) : null,
      ].filter((part): part is string => part !== null);
      if (parts.length === 0 && overview?.recentHiddenWorks != null) {
        parts.push(t('home.overviewHidden', { count: overview.recentHiddenWorks.length }));
      }
      return (
        <ListRow
          to={to}
          entry="m.overview"
          title={t('nav.overview')}
          subtitle={loading ? t('app.loading') : parts.join(' · ') || undefined}
        />
      );
    }
    case 'schedule':
      return <ScheduleRow to={to} site={site} />;
    case 'drawings':
      return <DrawingsRow to={to} siteId={site.id} />;
    case 'attendance': {
      const pending = overview?.pending;
      const summary =
        pending && pending.punches !== null && pending.anomalies !== null
          ? t('home.attendancePending', { punches: pending.punches, anomalies: pending.anomalies })
          : t('home.attendanceHint');
      return <ListRow to={to} entry="m.attendance" title={t('nav.attendance')} subtitle={summary} />;
    }
    default:
      return null;
  }
}

/**
 * 打卡（CR-082）：整块主色，左边「上班打卡 / 下班打卡」+ 现在的状态，右边现在几点。
 * 和打卡页的按钮读同一个来源（`useSiteLock`：服务端的 `me/shift` + 队列里还没传上去的那张卡）。
 * 原来读「今日打卡」，那是按自然日取的：夜班过了零点，这里说「还没打卡」，点进去按钮却是「下班」（CR-066）。
 */
function PunchHero({ to, siteId }: { to: string; siteId: number }) {
  const { t, i18n } = useTranslation();
  const lock = useSiteLock();
  const clockedIn = lock.open && (lock.lockedSiteId === null || lock.lockedSiteId === siteId);
  const now = useMinuteClock();

  return (
    <Link
      to={to}
      data-entry="m.punch"
      className="flex items-center justify-between gap-3 rounded-2xl bg-brand-600 px-5 py-4 text-white shadow-sm transition-colors active:bg-brand-700"
    >
      <span className="min-w-0">
        <span className="block text-title font-semibold">{clockedIn ? t('punch.clockOut') : t('punch.clockIn')}</span>
        <span className="mt-0.5 block text-body text-white/80">
          {lock.isLoading ? t('app.loading') : clockedIn ? t('home.clockedInHint') : t('home.notClockedInHint')}
        </span>
      </span>
      <span className="shrink-0 text-figure font-semibold tracking-tight tabular-nums">
        {new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false }).format(now)}
      </span>
    </Link>
  );
}

/** 进度表：今天在做的那一项（从进度表数据算，CR-080）。没发布就只写「今天该干到哪了」 */
function ScheduleRow({ to, site }: { to: string; site: Site }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['schedule', site.id, 'data'],
    queryFn: () => scheduleApi.get(site.id),
  });
  const schedule = query.data?.schedule ?? null;
  let subtitle = t('home.scheduleHint');
  if (schedule && schedule.status === ScheduleStatus.PUBLISHED) {
    const doing = activeOn(schedule.tasks, dayKeyInTimeZone(new Date(), site.timezone));
    subtitle =
      doing.length === 0
        ? t('home.scheduleNothingToday')
        : doing.length === 1
          ? t('home.scheduleToday', { name: doing[0]!.name })
          : t('home.scheduleTodayMore', { name: doing[0]!.name, count: doing.length - 1 });
  }
  return (
    <ListRow
      to={to}
      entry="m.schedule"
      title={t('nav.schedule')}
      subtitle={query.isLoading ? t('app.loading') : subtitle}
    />
  );
}

/** 图纸：份数一眼看到。能上传的人点进去就是管理图纸（CR-044：图纸页就是管理页） */
function DrawingsRow({ to, siteId }: { to: string; siteId: number }) {
  const { t } = useTranslation();
  const drawings = useQuery({
    queryKey: ['drawings', siteId, 'DRAWING'],
    queryFn: () => drawingsApi.list(siteId, { kind: 'DRAWING' }),
  });
  const count = drawings.data?.length ?? 0;
  return (
    <ListRow
      to={to}
      entry="m.drawings"
      title={t('nav.drawings')}
      subtitle={
        drawings.isLoading ? t('app.loading') : count === 0 ? t('drawings.empty') : t('home.drawingCount', { count })
      }
    />
  );
}

/** 每分钟走一次的钟：打卡块右边那个时间 */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let interval: number | undefined;
    const tick = () => setNow(new Date());
    // 对齐到下一个整分，之后每分钟一次
    const first = window.setTimeout(() => {
      tick();
      interval = window.setInterval(tick, 60_000);
    }, 60_000 - (Date.now() % 60_000));
    return () => {
      window.clearTimeout(first);
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, []);
  return now;
}

/** 早上好 / 下午好 / 晚上好：按这台手机的钟，工人在哪就是哪的早晚 */
function greeting(t: (key: string, opts?: Record<string, unknown>) => string, name: string): string {
  const hour = new Date().getHours();
  const key = hour < 12 ? 'home.greetMorning' : hour < 18 ? 'home.greetAfternoon' : 'home.greetEvening';
  return t(key, { name });
}
